<template>
    <DashboardLayout>
        <div class="flex flex-column" style="min-height: calc(100vh - 80px);">
        <Breadcrumb class="breadcrumb" :model="items" />
        <div class="card flex flex-column justify-center pt-4 px-5">
            <div v-if="viewStack.length > 1" class="mb-4">
                <Button label="Back" icon="pi pi-arrow-left" class="p-button-text back-button cursor-pointer "
                    @click="goBack" />
            </div>
            <div v-if="currentView.type === 'list'" class="notes-header-row flex flex-wrap justify-content-between align-items-center gap-2 mb-4 md:px-4">
                <Button
                    label="View Summaries & Transcripts"
                    icon="pi pi-external-link"
                    class="p-button-link p-0 notes-summary-btn"
                    @click="pushView('ai-summeries')"
                />
                <Button label="New Note" class="orange-button-small notes-new-btn" @click="handleNewNoteClick" />
            </div>
            <div v-if="currentView.type === 'list' && (Notes && Notes.length)">
                <NoteList :Notes="Notes" @view-note="pushView('View-Note', $event)" />
            </div>
            <div v-if="currentView.type === 'New-Note' || currentView.type === 'edit-note'">
                <NewNote :note="selectedNote" @refresh-notes="handleChanges" />
            </div>
            <div v-if="currentView.type === 'View-Note'">
                <ViewNote :note="selectedNote" @edit-note="pushView('edit-note', $event)"
                    @refresh-notes="handleChanges" />
            </div>

            <div v-if="currentView.type === 'ai-summeries'">
                <AiSummeries :Summaries="AISummeries" :Transcripts="Transcripts" 
                @view-summary="pushView('View-AI-Summary', $event)" 
                @view-transcript="pushView('View-AI-Transcript', $event)"/>
            </div>
            <div v-if="currentView.type === 'View-AI-Summary'">
                <AiSummaryView :data="selectedSummary" />
            </div>
            <div v-if="currentView.type === 'View-AI-Transcript'">
                <TranscriptView :data="selectedTranscript" />
            </div>
            <div v-else-if="currentView.type === 'list' && !(Notes && Notes.length)" class="flex flex-column px-6">
                <div class="flex flex-column  align-self-center ">
                    <span class="Caption text-gray-700 text-center justify-content-center align-self-center"> You don’t
                        have
                        any
                        notes yet, please click “New Note” to create a new note</span>
                </div>
            </div>
        </div>

        <!-- Disclaimer -->
        <div class="text-center w-full max-w-xl mx-auto py-4 px-6 mt-auto">
            <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #dc2626;">Disclaimer</h3>
            <p class="text-700 text-sm m-0">
                Al-generated content is provided for educational support only. It may contain inaccuracies and should not be relied upon as final academic answers.
            </p>
        </div>
        
        </div>
    </DashboardLayout>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import Breadcrumb from 'primevue/breadcrumb';
import Button from "primevue/button";
import NoteList from '@/components/dashboard/student/notes/noteList.vue'
import NewNote from '@/components/dashboard/student/notes/newNote.vue'
import ViewNote from '@/components/dashboard/student/notes/viewNote.vue'
import DashboardLayout from "@/layout/DashboardLayout.vue";
import { listNotes, checkNoteLimit, listAiSummeries, listTranscripts } from "@/api/student";
import { notifyError, notifyWarn } from "@/utils/notification";
import AiSummeries from "@/components/dashboard/student/notes/aiSummeries.vue";
import AiSummaryView from "@/components/dashboard/student/notes/AiSummaryView.vue";

const Notes = ref([]);
const AISummeries = ref([]);
const Transcripts = ref([]);
const selectedNote = ref([]);
const selectedSummary = ref();
const selectedTranscript = ref();

const viewStack = ref([
    { type: "list", label: "Notes", data: null }
]);
const items = computed(() => {
    return [
        { label: "Dashboard" },
        ...viewStack.value.map(v => ({ label: v.label }))
    ];
});
const currentView = computed(() =>
    viewStack.value[viewStack.value.length - 1]
);
function pushView(type, data) {
    if (type === "Notes") {
        viewStack.value.push({ type, label: "Notes", data });
    } if (type === "View-Note") {
        selectedNote.value = data;
        viewStack.value.push({ type, label: data.title, data });
    } if (type === "New-Note") {
        viewStack.value.push({ type, label: "Create-Note", data });
        selectedNote.value = null;
    } if (type === "edit-note") {
        selectedNote.value = data;
        viewStack.value.push({ type, label: "Update Note", data });
    } if (type === "ai-summeries") {
        viewStack.value.push({ type, label: "AI Summeries", data });
    } if (type === "View-AI-Summary") {
        selectedSummary.value = data;
        viewStack.value.push({ type, label: data.summary.studentSummary.title, data });
     } if (type === "View-AI-Transcript") {
        selectedTranscript.value = data;
        console.log(selectedTranscript.value);
        viewStack.value.push({ type, label: "Class Transcript", data });
     }
}
function goBack() {
    if (viewStack.value.length > 1) {
        viewStack.value.pop();
    }
}

const handleNewNoteClick = async () => {
    try {
        await checkNoteLimit();
        pushView('New-Note');
    } catch (error) {
        const responseData = error?.response?.data;
        const errorMsg = responseData?.message || "An error occurred. Please try again.";

        if (error?.response?.status === 403) {
            notifyWarn("Limit Reached", errorMsg);
        } else {
            notifyError(responseData?.status || "Error", errorMsg);
        }
    }
};

const fetchNotes = async () => {
    try {
        const res = await listNotes();
        Notes.value = res.data?.data || [];

        if (selectedNote.value) {
            selectedNote.value = Notes.value.find(note => note._id === selectedNote.value._id);
        }
    } catch (error) {
        const responseData = error?.response?.data;
        const errorMsg = responseData?.message || "An error occurred while fetching notes. Please try again.";

        if (error?.response?.status === 403) {
            notifyWarn("Subscription Required", errorMsg);
        } else {
            notifyError(responseData?.status || "Error", errorMsg);
        }
    }
}

const fetchAISummeries = async () => {
    try {
        const res = await listAiSummeries();
        AISummeries.value = res.data?.data || []; 
    } catch (error) {
        console.error("Error fetching AI summaries:", error);
    }
}

const fetchTranscripts = async () => {
    try {
        const res = await listTranscripts();
        Transcripts.value = res.data?.data || [];
    } catch (error) {
        console.error("Error fetching transcripts:", error);
    }
}

const handleChanges = () => {
    fetchNotes();
    goBack();
}

onMounted(() => {
    fetchNotes();
    fetchAISummeries();
    fetchTranscripts();
})
</script>

<style scoped>
.teal-button {
    padding: 8px 16px 8px 16px;
    height: 30px;
    font-size: 0.75rem;
}

.p-button-danger {
    padding: 10px 16px 10px 16px;
    height: 30px;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

.back-button {
    color: var(--gray-600);
}

.back-button:hover {
    color: var(--gray-600) !important;
    background-color: var(--gray-200) !important;
}

.back-button:active {
    color: var(--gray-600) !important;
}

.navigation {
    display: flex !important;
    flex-wrap: wrap !important;
    display: inline-block;
    padding: 10px;
}

.breadcrumb {
    display: flex !important;
    flex-wrap: wrap !important;
    overflow-x: hidden !important;

}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

@media (max-width: 767px) {
    .breadcrumb {
        font-size: 12px;
    }
}

@media (max-width: 576px) {
    .notes-header-row {
        flex-direction: column;
        align-items: center;
    }
    .notes-summary-btn {
        white-space: normal;
    }
    .notes-new-btn {
        width: 100%;
        max-width: 280px;
        justify-content: center;
        white-space: nowrap;
    }
}
</style>